import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  AlertCircle,
  Plus,
  Loader2,
  Car,
  X,
  Check,
  MapPin
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useViewportMapDataPaginated } from '../hooks/useViewportMapDataPaginated';
import WorkingMap from '../components/WorkingMap';
import { boundsAroundCenter, DEFAULT_MAP_CENTER } from '../utils/mapDefaults';
import { ErrorBanner } from '@/components/ErrorBanner';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import { useCampaignPriorities } from '@/hooks/useCampaignPriorities';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useDealerPreferences } from '@/hooks/useDealerPreferences';
import { findZipCodesInRadius } from '@/utils/geoUtils';
import { RecallVehicle } from '../services/recallAPI';
import { NotesIndicator } from '@/components/NotesIndicator';
import { DealerNotesPanel } from '@/components/DealerNotesPanel';
import { useDealerNotes } from '@/hooks/useDealerNotes';
import { DealerConfigError } from '@/lib/supabase/dealerConfig';
import { PausedCampaignIndicator } from '@/components/PausedCampaignIndicator';
import { AdvanceNoticeIndicator } from '@/components/AdvanceNoticeIndicator';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { describeActionError } from '@/lib/errors/lifecycleRefusal';
import { useRecallFilterOptionsQuery } from '../hooks/useRecallListQuery';
import { useToast } from '@/hooks/use-toast';
import { InlineError } from '@/components/InlineError';
import { useRecallAssignmentStatus } from '@/hooks/useRecallAssignmentStatus';
import { SecurityBoundary } from '@/lib/security';
import { PermissionScope } from '@/types';
import { logDataAccess, logAdminAction } from '@/lib/security/audit';
import { logUserActivity } from '@/lib/activity/userActivity';
import { RemoveRecallDialog, RemovalReason } from '@/components/RemoveRecallDialog';
import { statusForRemovalReason, reasonTextForRemoval } from '@/lib/lifecycle/releaseDisposition';
import { updateLifecycleOnManualStatusChange } from '@/lib/supabase/recallLifecycle';
import { routeTrackingAPI } from '@/lib/supabase/routeTracking';
import { ManualStopForm } from '../components/ManualStopForm';
import { ManualStopsList } from '../components/ManualStopsList';
import type { ManualStopSubmission } from '@/lib/validation/manualStopValidation';
import { useIsDispatcher } from '@/hooks/useIsDispatcher';
import { getStopLocationKey, isCustomerStop } from '@/lib/manualStops/locationKey';
import type { ManualStopKindFilter } from '../types/map.types';
import { removeCache } from '@/lib/utils/cache';
import { useTechniciansQuery } from '@/hooks/useTechniciansQuery';
import { useDealerAssignmentSummaries } from '../hooks/useDealerAssignmentSummaries';
import { getTechnicianColor as resolveTechnicianColor } from '../utils/technicianColors';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MapLegend } from '../components/MapLegend';
import { RecallFilterBar } from '../components/RecallFilterBar';
import { usePersistedFilters } from '../hooks/usePersistedFilters';

// Type definitions for dealer data
/** RPC rows carry snapshot_date, which RecallVehicle does not declare. */
type SnapshotDated = { snapshot_date?: string | null };

interface VehicleData {
  vin: string;
  carYear: number;
  makeName: string;
  modelName: string;
  recalls: RecallVehicle[];
}

interface DealerCoordinates {
  latitude: number;
  longitude: number;
}

interface DealerLocation {
  dealerId: string;
  dealerName: string;
  name?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string | null;
  coordinates: DealerCoordinates;
  vehicleCount: number;
  recallCount: number;
  campaignCount: number;
  highestSkillLevel: number | null;
  vehicles: VehicleData[];
}

/** How long the map waits for the tenant before opening on the default center anyway. */
const TENANT_WAIT_BEFORE_MAP_MS = 8000;

// OPTIMIZATION: Debounce hook to reduce expensive filter computations
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Format phone number for display
 * Handles: 1234567890, 123-456-7890, (123) 456-7890, +1 123 456 7890
 * Output: (123) 456-7890
 */
const formatPhoneNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

/**
 * SimpleRecallMapPage - Main recall map visualization component
 *
 * CRITICAL: Map Position Preservation
 * -----------------------------------
 * This component uses a special loading state management to prevent the map from
 * unmounting and resetting position when navigating to areas without recalls.
 *
 * Key Implementation Details:
 * 1. The `hasInitiallyLoaded` flag tracks if data has been loaded at least once
 * 2. The loading screen ONLY shows on initial page load (when hasInitiallyLoaded = false)
 * 3. Once loaded, the map component stays mounted even when loading new viewport data
 * 4. This prevents the map from resetting to Florida when dragging to empty areas
 *
 * DO NOT MODIFY the loading condition without understanding this behavior!
 * The condition `loading && !hasInitiallyLoaded && recalls.length === 0` is critical.
 *
 * @component
 */
/* eslint-disable max-lines-per-function, complexity, max-len */
type MapBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

const SimpleRecallMapPage = () => {
  const { session } = useAuth();
  // Staging manual stops is dispatcher-only (stage_manual_stops_v1 enforces it too).
  const { isDispatcher } = useIsDispatcher();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id || null;

  // Use tenant's configured coordinates as the map center (falls back to Florida in WorkingMap)
  const mapCenter: [number, number] | undefined =
    currentTenant?.latitude && currentTenant?.longitude
      ? [currentTenant.latitude, currentTenant.longitude]
      : undefined;
  // Read by the initial-load fallback timer; the tenant can resolve after the timer starts.
  const mapCenterRef = useRef(mapCenter);
  mapCenterRef.current = mapCenter;

  // The map reads its center only when it mounts, so hold that first mount until the
  // tenant is known and it opens on the dealership instead of flashing the default.
  // Latched: once allowed, the map is never unmounted again (see map-component rules).
  // The timeout keeps a tenant that never loads from blocking the map entirely.
  const [tenantWaitExpired, setTenantWaitExpired] = useState(false);
  useEffect(() => {
    if (currentTenant) return undefined;
    const timer = setTimeout(() => setTenantWaitExpired(true), TENANT_WAIT_BEFORE_MAP_MS);
    return () => clearTimeout(timer);
  }, [currentTenant]);
  const mapMountAllowedRef = useRef(false);
  if (currentTenant || tenantWaitExpired) mapMountAllowedRef.current = true;

  const {
    recalls,
    allRecallsCache,
    loading,
    error,
    loadViewportData,
    isLoadingMore,
    preferencesLoaded,
    isCacheBuilding,
    hasCachedData,
    fullLoadError,
    refetchFullDataset
  } = useViewportMapDataPaginated(session?.user?.id, tenantId);
  const dataLoadError = fullLoadError ?? error;

  // Note: useDealerAggregates is no longer used for map display.
  // Internal tenants now load full data like non-internal tenants, so recall details
  // are always available when expanding a dealer (no "details loading" message).
  // The aggregate fast path has been removed to ensure consistent behavior.

  const {
    addRecall,
    removeRecall,
    selectedRecalls,
    isAuthenticated,
    stagedManualStops,
    addManualStop,
    removeManualStop
  } = useSelectedRecalls();
  const { campaignStatus, campaignStatusReasons } = useCampaignPriorities(session?.user?.id);
  const { getDealerPreference } = useDealerPreferences(session?.user?.id);
  const { getRecallStatus, completedManualStops, removedRecallKeys, statusMap } =
    useRecallAssignmentStatus();
  const { data: techniciansData } = useTechniciansQuery();
  const technicians = useMemo(
    () => (techniciansData?.technicians || []) as Array<{ id: string; full_name: string }>,
    [techniciansData]
  );
  const { toast } = useToast();

  // Technician visualization state (drives marker dimming + optional coloring)
  const [selectedTechnicianIds, setSelectedTechnicianIds] = useState<string[]>([]);
  const [colorByTechnician, setColorByTechnician] = useState(false);

  // Stable color resolver bound to the loaded technician roster
  const getTechnicianColor = useCallback(
    (technicianId: string) => resolveTechnicianColor(technicianId, technicians),
    [technicians]
  );

  // Technician color chips for the legend (only when color-by-tech is on)
  const legendTechnicians = useMemo(
    () =>
      colorByTechnician
        ? technicians.map(t => ({ id: t.id, name: t.full_name, color: getTechnicianColor(t.id) }))
        : [],
    [colorByTechnician, technicians, getTechnicianColor]
  );

  const [selectedDealer, setSelectedDealer] = useState<string | null>(null);
  // Which dealer is hoisted to the top of the list. Driven only by map-bubble
  // clicks, where the card may be far offscreen. Kept separate from
  // `selectedDealer` so clicking a card in the list never reorders the list.
  const [pinnedDealer, setPinnedDealer] = useState<string | null>(null);
  const [expandedDealers, setExpandedDealers] = useState<Set<string>>(new Set());
  const dealerCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollAnchorRef = useRef<{ dealerId: string; top: number } | null>(null);
  const scrollIntoViewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [mapFilterActive, setMapFilterActive] = useState(false);
  const [flyToTarget, setFlyToTarget] = useState<{ lat: number; lng: number; zoom: number } | null>(
    null
  );
  const [loadingRecalls, setLoadingRecalls] = useState<Set<string>>(new Set());
  const [isLoadingViewport, setIsLoadingViewport] = useState(false);
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [zipRadius, setZipRadius] = useState<string>('0');
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Notes management state (dealer-level)
  const [notesOpen, setNotesOpen] = useState<Set<string>>(new Set());

  // Phone copy feedback state
  const [copiedPhone, setCopiedPhone] = useState<string | null>(null);

  // Recall removal state
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const [recallToRemove, setRecallToRemove] = useState<RecallVehicle | null>(null);
  const [removedRecalls, setRemovedRecalls] = useState<Set<string>>(new Set());
  const [fadingOutRecalls, setFadingOutRecalls] = useState<Set<string>>(new Set());

  // Right-side panel: controls the Add Stop Sheet
  const [addStopSheetOpen, setAddStopSheetOpen] = useState(false);
  // When a manual-stop map bubble is clicked, store the location name so the
  // merged list can scroll to and expand the matching LocationGroup
  const [scrollToStopLocation, setScrollToStopLocation] = useState<string | null>(null);
  // Filter sidebar to show only manual stops (for easy removal)
  const [showStopsOnly, setShowStopsOnly] = useState(false);

  // Build map of assigned manual stops with technician info
  const assignedManualStops = useMemo(() => {
    const map = new Map<string, { technicianName: string }>();
    stagedManualStops.forEach(stop => {
      const key = `${stop.vin}|${stop.campaign_number}`;
      const status = getRecallStatus(stop.vin, stop.campaign_number);
      if (status.status === 'assigned' && status.technicianName) {
        map.set(key, { technicianName: status.technicianName });
      }
    });
    return map;
  }, [stagedManualStops, getRecallStatus]);

  // Manual stops visible on the map — exclude completed; NOT viewport-filtered
  // (the map has its own viewport; double-filtering causes marker churn on pan)
  const [stopKindFilter, setStopKindFilter] = useState<ManualStopKindFilter>('all');

  // Open (not completed) manual stops, before the Business / Customer filter
  const openManualStops = useMemo(
    () =>
      stagedManualStops.filter(
        stop => !completedManualStops.has(`${stop.vin}|${stop.campaign_number}`)
      ),
    [stagedManualStops, completedManualStops]
  );

  const manualStopKindCounts = useMemo(() => {
    const customerKeys = new Set<string>();
    const businessKeys = new Set<string>();
    openManualStops.forEach(stop =>
      (isCustomerStop(stop) ? customerKeys : businessKeys).add(getStopLocationKey(stop))
    );
    return { business: businessKeys.size, customer: customerKeys.size };
  }, [openManualStops]);

  const visibleManualStops = useMemo(() => {
    return openManualStops
      .filter(
        stop => stopKindFilter === 'all' || (stopKindFilter === 'customer') === isCustomerStop(stop)
      )
      .map(stop => {
        // Attach the assigned technician so the map can highlight manual stops
        // the same way it highlights recalls (same status map, same color).
        const status = getRecallStatus(stop.vin ?? '', stop.campaign_number ?? '');
        return status.status !== 'available' && status.technicianId
          ? { ...stop, technicianId: status.technicianId }
          : stop;
      });
  }, [openManualStops, stopKindFilter, getRecallStatus]);

  // Manual stops shown in the Stops tab list — applies the "Filtered by Map Area"
  // filter on top of the map-visible set (mirrors visibleDealerLocations behavior)
  const viewportFilteredManualStops = useMemo(() => {
    if (!mapFilterActive || !mapBounds) return visibleManualStops;
    return visibleManualStops.filter(stop => {
      const lat = stop.dealer_latitude;
      const lon = stop.dealer_longitude;
      return (
        lat >= mapBounds.south &&
        lat <= mapBounds.north &&
        lon >= mapBounds.west &&
        lon <= mapBounds.east
      );
    });
  }, [visibleManualStops, mapBounds, mapFilterActive]);

  // Unique manual-stop locations (households / businesses), keyed by getStopLocationKey —
  // every customer stop is named 'Customer stop', so names would undercount.
  const uniqueStopLocationNames = useMemo(() => {
    const keys = new Set<string>();
    viewportFilteredManualStops.forEach(stop => keys.add(getStopLocationKey(stop)));
    return keys;
  }, [viewportFilteredManualStops]);

  const loadViewportDataRef = useRef(loadViewportData);
  const mapBoundsRef = useRef<MapBounds | null>(null);
  const mapFilterActiveRef = useRef(false);
  const initialLoadTriggeredRef = useRef(false);

  useEffect(() => {
    loadViewportDataRef.current = loadViewportData;
  }, [loadViewportData]);

  useEffect(() => {
    mapBoundsRef.current = mapBounds;
  }, [mapBounds]);

  useEffect(() => {
    mapFilterActiveRef.current = mapFilterActive;
  }, [mapFilterActive]);

  // Log page view on mount
  useEffect(() => {
    logUserActivity({
      category: 'recall_selection',
      action: 'view_map'
    });
  }, []);

  // Filter states
  const [filters, setFilters] = useState<{
    vin: string;
    make: string;
    model: string;
    year: string;
    dealer: string;
    campaigns: string[];
    city: string;
    zip: string;
    mobile_eligible: string;
    region: string;
    campaign_status: string;
    priority: string;
    assignment_state: 'all' | 'unassigned' | 'in_route';
  }>({
    vin: '',
    make: '',
    model: '',
    year: '',
    dealer: '',
    campaigns: [],
    city: 'all',
    zip: 'all',
    mobile_eligible: 'all',
    region: '',
    campaign_status: 'all',
    priority: 'all',
    assignment_state: 'all'
  });

  // OPTIMIZATION: Debounce filters to prevent expensive recalculations on every keystroke
  // Instant filters (dropdowns) vs delayed filters (text inputs)
  const debouncedTextFilters = useDebounce(
    {
      vin: filters.vin,
      make: filters.make,
      model: filters.model,
      year: filters.year,
      dealer: filters.dealer,
      region: filters.region
    },
    300 // 300ms debounce for text inputs
  );

  // Combine instant and debounced filters
  const activeFilters = useMemo(
    () => ({
      ...debouncedTextFilters,
      campaigns: filters.campaigns,
      city: filters.city,
      zip: filters.zip,
      mobile_eligible: filters.mobile_eligible,
      campaign_status: filters.campaign_status,
      priority: filters.priority,
      assignment_state: filters.assignment_state
    }),
    [
      debouncedTextFilters,
      filters.campaigns,
      filters.city,
      filters.zip,
      filters.mobile_eligible,
      filters.campaign_status,
      filters.priority,
      filters.assignment_state
    ]
  );

  // Get ZIP codes within radius when a specific ZIP and radius are selected
  const zipCodesInRadius = useMemo(() => {
    const selectedZip = activeFilters.zip;
    const radius = parseInt(zipRadius);

    if (!selectedZip || selectedZip === 'all' || radius <= 0 || !recalls.length) {
      return [selectedZip].filter(Boolean);
    }

    return findZipCodesInRadius(selectedZip, recalls, radius);
  }, [activeFilters.zip, zipRadius, recalls]);

  // Staged (selected-for-route) recall keys, mirroring useRecallAssignmentStatus.createKey
  const selectedKeySet = useMemo(() => {
    const set = new Set<string>();
    selectedRecalls.forEach(r => {
      if (r.vin && r.campaign_number) set.add(`${r.vin}|${r.campaign_number}`);
    });
    return set;
  }, [selectedRecalls]);

  // Filter the recalls data based on DEBOUNCED filters
  const filteredRecalls = useMemo(() => {
    // When VIN filter is active, search across ALL recalls (not just viewport)
    const isVinSearch = Boolean(activeFilters.vin);
    const dataSource = isVinSearch && allRecallsCache ? allRecallsCache : recalls;

    if (!dataSource.length) return [];

    const filtered = dataSource.filter(recall => {
      // Exclude recalls that are done/removed in the route tracker (completed or
      // cancelled / vehicle-sold). They should only live in the route tracker's
      // completed section. (completedManualStops is a subset of removedRecallKeys.)
      const recallKey = `${recall.vin}|${recall.campaign_number}`;
      if (removedRecallKeys.has(recallKey)) {
        return false;
      }

      // VIN filter (debounced)
      if (
        activeFilters.vin &&
        !recall.vin?.toLowerCase().includes(activeFilters.vin.toLowerCase())
      ) {
        return false;
      }

      // Make filter (debounced)
      if (
        activeFilters.make &&
        !recall.makeName?.toLowerCase().includes(activeFilters.make.toLowerCase())
      ) {
        return false;
      }

      // Model filter (debounced)
      if (
        activeFilters.model &&
        !recall.modelName?.toLowerCase().includes(activeFilters.model.toLowerCase())
      ) {
        return false;
      }

      // Year filter (debounced)
      if (activeFilters.year && recall.carYear?.toString() !== activeFilters.year) {
        return false;
      }

      // Dealer filter (debounced)
      if (
        activeFilters.dealer &&
        !recall.seller_name?.toLowerCase().includes(activeFilters.dealer.toLowerCase())
      ) {
        return false;
      }

      // Campaign filter (instant) - multi-select
      if (
        activeFilters.campaigns?.length &&
        !activeFilters.campaigns.includes(recall.campaign_number)
      ) {
        return false;
      }

      // City filter (instant)
      if (activeFilters.city !== 'all') {
        if (recall.dealer_city !== activeFilters.city) {
          return false;
        }
      }

      // ZIP code filter with radius support (instant)
      if (activeFilters.zip !== 'all') {
        if (!zipCodesInRadius.includes(recall.dealer_postal_code)) {
          return false;
        }
      }

      // Mobile eligible filter (instant)
      if (activeFilters.mobile_eligible !== 'all') {
        const isMobileEligible = recall.mobile_eligible;
        if (activeFilters.mobile_eligible === 'true' && !isMobileEligible) return false;
        if (activeFilters.mobile_eligible === 'false' && isMobileEligible) return false;
      }

      // Region filter (debounced)
      if (
        activeFilters.region &&
        !recall.seller_region?.toLowerCase().includes(activeFilters.region.toLowerCase())
      ) {
        return false;
      }

      // Priority/Skill level filter (instant) - uses campaign configuration skill levels
      if (activeFilters.priority && activeFilters.priority !== 'all') {
        const recallPriority = recall.priority || 3; // Default to low (3) if not set
        // Compare as strings since activeFilters.priority is a string from the Select component
        if (recallPriority.toString() !== activeFilters.priority.toString()) {
          return false;
        }
      }

      // Campaign status filter (instant)
      if (activeFilters.campaign_status && activeFilters.campaign_status !== 'all') {
        if (!recall.campaign_number) {
          return false;
        }
        const campaignLevelStatus = campaignStatus[recall.campaign_number] || 'Available';

        // Recall-level advance notice upgrades "Available" → "Advance Notice",
        // but does NOT override user-set "Paused"
        const effectiveStatus =
          recall.has_advance_notice_recalls && campaignLevelStatus !== 'Paused'
            ? 'Advance Notice'
            : campaignLevelStatus;

        if (effectiveStatus !== activeFilters.campaign_status) {
          return false;
        }
      }

      // Assignment-state filter (instant) — lets the user focus on unassigned work
      if (activeFilters.assignment_state && activeFilters.assignment_state !== 'all') {
        const info = statusMap.get(recallKey);
        const inRoute = !!info?.routeId;
        const assignedTech = !!info && info.status !== 'available' && !!info.technicianId;
        // "In a route" covers any recall that's been actioned (route, technician,
        // or staged) — assigning a recall creates both, so they're one concept.
        const handled = inRoute || assignedTech || selectedKeySet.has(recallKey);

        if (activeFilters.assignment_state === 'unassigned' && handled) return false;
        if (activeFilters.assignment_state === 'in_route' && !handled) return false;
      }

      // Dealer preference filter - exclude dealers marked as excluded in dealer config
      // Normalize dealer name to handle whitespace and case differences
      if (recall.seller_name) {
        const normalizedDealerName = recall.seller_name.trim();
        const dealerPreference = getDealerPreference(normalizedDealerName);
        if (dealerPreference.is_included === false) {
          return false; // Exclude this recall if dealer is marked as excluded
        }
      }

      return true;
    });

    return filtered;
  }, [
    recalls,
    allRecallsCache,
    activeFilters,
    zipCodesInRadius,
    campaignStatus,
    getDealerPreference,
    removedRecallKeys,
    statusMap,
    selectedKeySet
  ]);

  // Auto-center map on VIN search results
  useEffect(() => {
    if (!activeFilters.vin || filteredRecalls.length === 0) {
      setFlyToTarget(null);
      return;
    }

    // Check if any matching recalls are already in the current viewport
    let hasResultsInViewport = false;
    if (mapBounds) {
      hasResultsInViewport = filteredRecalls.some(recall => {
        const lat = recall.dealer_latitude;
        const lng = recall.dealer_longitude;
        if (lat == null || lng == null) return false;
        return (
          lat >= mapBounds.south &&
          lat <= mapBounds.north &&
          lng >= mapBounds.west &&
          lng <= mapBounds.east
        );
      });
    }

    if (!hasResultsInViewport) {
      const firstMatch = filteredRecalls.find(
        r => r.dealer_latitude != null && r.dealer_longitude != null
      );
      if (firstMatch?.dealer_latitude != null && firstMatch?.dealer_longitude != null) {
        setFlyToTarget({
          lat: firstMatch.dealer_latitude,
          lng: firstMatch.dealer_longitude,
          zoom: 12
        });
      }
    }
  }, [activeFilters.vin, filteredRecalls, mapBounds]);

  // Aggregate recalls by dealer location for the map
  // NOTE: For both internal and non-internal tenants, we always use full recall data.
  // This ensures every dealer has vehicle details and we never show the
  // "Recall details will appear once data has finished loading" message.
  const dealerLocations = useMemo(() => {
    // STANDARD PATH: Compute from filtered recalls (accurate data with full vehicle details)
    const dealerMap = new Map<string, DealerLocation>();
    const vinMap = new Map<string, VehicleData>();

    filteredRecalls.forEach(recall => {
      if (!recall.dealer_latitude || !recall.dealer_longitude) {
        return;
      }

      // Match aggregate RPC dealer_key format (name|lat|lon rounded to 4 decimals) so the same dealer
      // keeps the same id when switching from aggregate (fast path) to recall-derived (standard path).
      const dealerId = `${recall.seller_name}|${Number(recall.dealer_latitude).toFixed(4)}|${Number(recall.dealer_longitude).toFixed(4)}`;

      if (!dealerMap.has(dealerId)) {
        // Build address from recall data
        const addressParts = [];
        if (recall.dealer_street && recall.dealer_street.trim()) {
          addressParts.push(recall.dealer_street.trim());
        }
        if (recall.dealer_city && recall.dealer_city.trim()) {
          addressParts.push(recall.dealer_city.trim());
        }
        if (recall.dealer_region && recall.dealer_region.trim()) {
          addressParts.push(recall.dealer_region.trim());
        }
        if (recall.dealer_postal_code && recall.dealer_postal_code.trim()) {
          addressParts.push(recall.dealer_postal_code.trim());
        }

        const fullAddress =
          addressParts.length > 0 ? addressParts.join(', ') : 'Address not available';

        dealerMap.set(dealerId, {
          dealerId,
          dealerName: recall.seller_name || 'Unknown Dealer',
          name: recall.seller_name || 'Unknown Dealer', // For WorkingMap compatibility
          address: fullAddress,
          city: recall.dealer_city || '',
          state: recall.dealer_region || '',
          zip: recall.dealer_postal_code || '',
          phone: recall.dealer_phone || null,
          coordinates: {
            latitude: recall.dealer_latitude,
            longitude: recall.dealer_longitude
          },
          vehicleCount: 0,
          recallCount: 0,
          campaignCount: 0,
          highestSkillLevel: null,
          vehicles: []
        });
      }

      const dealer = dealerMap.get(dealerId);
      if (!dealer) {
        return;
      }

      // Group recalls by VIN
      const vehicleKey = `${dealerId}_${recall.vin}`;
      if (!vinMap.has(vehicleKey)) {
        const newVehicle: VehicleData = {
          vin: recall.vin,
          carYear: recall.carYear,
          makeName: recall.makeName,
          modelName: recall.modelName,
          recalls: []
        };
        vinMap.set(vehicleKey, newVehicle);
        dealer.vehicles.push(newVehicle);
        dealer.vehicleCount += 1;
      }

      const vehicle = vinMap.get(vehicleKey);
      if (vehicle) {
        // Always add the recall first - we'll deduplicate at the end
        // This ensures we have all recalls before comparing snapshot_date
        vehicle.recalls.push(recall);
      }
    });

    // Post-process: Deduplicate recalls by VIN + campaign_number, keeping only latest snapshot_date
    dealerMap.forEach(dealer => {
      dealer.vehicles.forEach(vehicle => {
        // Normalize and group recalls by VIN + campaign_number
        // Use normalized keys to handle any whitespace/case issues
        const recallMap = new Map<string, RecallVehicle>();

        vehicle.recalls.forEach(recall => {
          // Normalize VIN and campaign_number for consistent comparison
          const normalizedVin = (recall.vin || '').trim().toUpperCase();
          const normalizedCampaign = (recall.campaign_number || '').trim().toUpperCase();
          const key = `${normalizedVin}-${normalizedCampaign}`;

          const existing = recallMap.get(key);

          if (!existing) {
            // First occurrence of this VIN + campaign_number
            recallMap.set(key, recall);
          } else {
            // Duplicate found - compare snapshot_date
            // snapshot_date comes from the RPC row but is not part of RecallVehicle
            const existingSnapshot = (existing as SnapshotDated).snapshot_date;
            const newSnapshot = (recall as SnapshotDated).snapshot_date;
            const existingDate = existingSnapshot ? new Date(existingSnapshot) : null;
            const newDate = newSnapshot ? new Date(newSnapshot) : null;

            if (newDate && existingDate) {
              // Both have dates - keep the latest
              if (newDate > existingDate) {
                recallMap.set(key, recall);
              }
            } else if (newDate && !existingDate) {
              // New has date, existing doesn't - prefer new
              recallMap.set(key, recall);
            }
            // Otherwise keep existing (has date or first encountered)
          }
        });

        // Replace vehicle.recalls with deduplicated list
        vehicle.recalls = Array.from(recallMap.values());
      });

      // Recalculate recall count after deduplication
      dealer.recallCount = dealer.vehicles.reduce((sum, v) => sum + v.recalls.length, 0);
    });

    return Array.from(dealerMap.values());
  }, [filteredRecalls]);

  // Workability: advance-notice and paused recalls aren't serviceable yet, so
  // they're excluded from the "% handled" math (only Available counts).
  const isRecallWorkable = useCallback(
    (recall: { campaign_number: string; has_advance_notice_recalls?: boolean }) => {
      const level = campaignStatus[recall.campaign_number] || 'Available';
      const effective =
        recall.has_advance_notice_recalls && level !== 'Paused' ? 'Advance Notice' : level;
      return effective === 'Available';
    },
    [campaignStatus]
  );

  // Enrich dealers with assignment rollups (drives the marker progress ring +
  // done-state) and a cheap version counter for the map memo comparator.
  const {
    dealerLocationsWithSummary,
    totalsSummary: assignmentTotals,
    summaryVersion
  } = useDealerAssignmentSummaries(dealerLocations, statusMap, selectedRecalls, isRecallWorkable);

  // Get filter options using the same optimized RPC function as RecallListPage
  const filterOptionsQuery = useRecallFilterOptionsQuery();

  // Get unique options for filter dropdowns (efficiently loaded via React Query)
  const uniqueCampaigns = useMemo(() => {
    // Use RPC data if available, otherwise fall back to loaded recalls
    if (filterOptionsQuery.data?.campaigns && filterOptionsQuery.data.campaigns.length > 0) {
      return filterOptionsQuery.data.campaigns;
    }
    // Fallback to client-side extraction if RPC data not available yet
    if (!recalls.length) return [];
    const campaigns = new Set(recalls.map(recall => recall.campaign_number).filter(Boolean));
    return Array.from(campaigns).sort();
  }, [filterOptionsQuery.data?.campaigns, recalls]);

  const uniqueCities = useMemo(() => {
    // Use RPC data if available, otherwise fall back to loaded recalls
    if (filterOptionsQuery.data?.cities && filterOptionsQuery.data.cities.length > 0) {
      return filterOptionsQuery.data.cities;
    }
    // Fallback to client-side extraction if RPC data not available yet
    if (recalls.length) {
      const cities = new Set(recalls.map(recall => recall.dealer_city).filter(Boolean));
      return Array.from(cities).sort();
    }
    // In clustered mode, get cities from dealer locations
    const cities = new Set(dealerLocations.map(dealer => dealer.city).filter(Boolean));
    return Array.from(cities).sort();
  }, [filterOptionsQuery.data?.cities, recalls, dealerLocations]);

  const uniqueZipCodes = useMemo(() => {
    // Use RPC data if available, otherwise fall back to loaded recalls
    if (filterOptionsQuery.data?.zipCodes && filterOptionsQuery.data.zipCodes.length > 0) {
      return filterOptionsQuery.data.zipCodes;
    }
    // Fallback to client-side extraction if RPC data not available yet
    if (recalls.length) {
      const zipCodes = new Set(recalls.map(recall => recall.dealer_postal_code).filter(Boolean));
      return Array.from(zipCodes).sort();
    }
    // In clustered mode, get ZIP codes from dealer locations
    const zipCodes = new Set(dealerLocations.map(dealer => dealer.zip).filter(Boolean));
    return Array.from(zipCodes).sort();
  }, [filterOptionsQuery.data?.zipCodes, recalls, dealerLocations]);

  // Get unique dealer names for notes loading
  const dealerNames = useMemo(() => {
    const uniqueDealers = new Set<string>();

    if (filteredRecalls.length) {
      // Use detailed recall data when available
      filteredRecalls.forEach(recall => {
        if (recall.seller_name) {
          uniqueDealers.add(recall.seller_name);
        }
      });
    } else {
      // Use clustered dealer data
      dealerLocations.forEach(dealer => {
        const dealerName = dealer.dealerName || dealer.name;
        if (dealerName) {
          uniqueDealers.add(dealerName);
        }
      });
    }

    return Array.from(uniqueDealers);
  }, [dealerLocations, filteredRecalls]);

  // Initialize dealer notes hook
  const {
    hasAdvisorNotes,
    hasTechnicianNotes,
    getNotes,
    updateNotes,
    readOnly: notesReadOnly,
    loading: notesLoading
  } = useDealerNotes({
    ...(session?.user?.id && { userId: session.user.id }),
    dealerNames
  });

  // Filter dealer locations by map bounds if active
  // When VIN search is active, show all matching dealers regardless of viewport
  // Use the assignment-enriched dealers so the right-panel cards carry the same
  // `assignmentSummary` that drives the map bubble progress rings.
  const visibleDealerLocations = useMemo(() => {
    if (activeFilters.vin) {
      return dealerLocationsWithSummary;
    }

    if (!mapFilterActive || !mapBounds) {
      return dealerLocationsWithSummary;
    }

    return dealerLocationsWithSummary.filter((dealer: DealerLocation) => {
      if (!dealer.coordinates?.latitude || !dealer.coordinates?.longitude) {
        return false;
      }

      const lat = dealer.coordinates.latitude;
      const lon = dealer.coordinates.longitude;

      return (
        lat >= mapBounds.south &&
        lat <= mapBounds.north &&
        lon >= mapBounds.west &&
        lon <= mapBounds.east
      );
    });
  }, [dealerLocationsWithSummary, mapBounds, mapFilterActive, activeFilters.vin]);

  // Filtered dealer locations based on showStopsOnly toggle
  const filteredDealerLocations = useMemo(() => {
    if (showStopsOnly) return [];
    return visibleDealerLocations;
  }, [visibleDealerLocations, showStopsOnly]);

  // Pin a dealer to the top of the list so it's always in view when chosen from
  // the map. Only map-bubble clicks set `pinnedDealer`; list clicks leave the
  // order untouched so the list never moves under the user's cursor.
  const orderedDealerLocations = useMemo(() => {
    if (!pinnedDealer) return filteredDealerLocations;
    const index = filteredDealerLocations.findIndex(d => d.dealerId === pinnedDealer);
    if (index <= 0) return filteredDealerLocations;
    const pinned = filteredDealerLocations[index];
    if (!pinned) return filteredDealerLocations;
    return [pinned, ...filteredDealerLocations.filter((_, i) => i !== index)];
  }, [filteredDealerLocations, pinnedDealer]);

  // Record where a dealer card currently sits inside the scroll container, so the
  // layout effect below can hold it at that exact spot across the re-render.
  const captureScrollAnchor = useCallback((dealerId: string) => {
    const container = listScrollRef.current;
    const card = dealerCardRefs.current.get(dealerId);
    if (!container || !card) return;
    scrollAnchorRef.current = {
      dealerId,
      top: card.getBoundingClientRect().top - container.getBoundingClientRect().top
    };
  }, []);

  // Expanding a card also collapses the previously open one (single-open
  // accordion). When that card sits above, losing its height would yank the
  // clicked card upward. Re-measure before paint and absorb the difference.
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) return;
    scrollAnchorRef.current = null;

    const container = listScrollRef.current;
    const card = dealerCardRefs.current.get(anchor.dealerId);
    if (!container || !card) return;

    const nextTop = card.getBoundingClientRect().top - container.getBoundingClientRect().top;
    const delta = nextTop - anchor.top;
    if (delta !== 0) {
      container.scrollTop += delta;
    }
  }, [selectedDealer, expandedDealers]);

  // Select a dealer card (highlight, optionally expand, optionally scroll into
  // view). When `expand` is true (card clicks) selection doubles as a single-open
  // accordion: it expands the dealer and collapses the previously selected one.
  // Map-bubble clicks pass `expand: false, scroll: true` so they only select and
  // bring the card into view; card clicks pass neither, so the list holds still.
  const selectDealerCard = useCallback(
    (dealerId: string, options?: { expand?: boolean; scroll?: boolean }) => {
      const expand = options?.expand ?? true;
      const scroll = options?.scroll ?? false;

      if (selectedDealer === dealerId) {
        setSelectedDealer(null);
        if (expand) {
          setExpandedDealers(prev => {
            if (!prev.has(dealerId)) return prev;
            const next = new Set(prev);
            next.delete(dealerId);
            return next;
          });
        }
        return;
      }

      const previouslySelected = selectedDealer;
      setSelectedDealer(dealerId);

      if (expand) {
        setExpandedDealers(prev => {
          const next = new Set(prev);
          if (previouslySelected) next.delete(previouslySelected);
          next.add(dealerId);
          return next;
        });

        // Lazy-load vehicle details for the newly expanded dealer (aggregate fast path),
        // mirroring toggleDealerExpansion so the panel can show recall details.
        if (mapBoundsRef.current) {
          const dealer = dealerLocations.find(d => d.dealerId === dealerId);
          if (dealer && (dealer.vehicles || []).length === 0) {
            loadViewportDataRef.current(mapBoundsRef.current);
          }
        }
      }

      // Map-bubble path only: the card may be far offscreen (or freshly pinned to
      // the top), so scroll it into view once it has re-rendered.
      if (scroll) {
        if (scrollIntoViewTimerRef.current) {
          clearTimeout(scrollIntoViewTimerRef.current);
        }
        scrollIntoViewTimerRef.current = setTimeout(() => {
          scrollIntoViewTimerRef.current = null;
          const dealerCardElement = dealerCardRefs.current.get(dealerId);
          if (dealerCardElement) {
            dealerCardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 0);
      }
    },
    [dealerLocations, selectedDealer]
  );

  useEffect(() => {
    return () => {
      if (scrollIntoViewTimerRef.current) {
        clearTimeout(scrollIntoViewTimerRef.current);
      }
    };
  }, []);

  const handleDealerClick = (dealer: DealerLocation) => {
    // Hold the clicked card at its current on-screen position across the
    // expand/collapse re-render.
    captureScrollAnchor(dealer.dealerId);
    selectDealerCard(dealer.dealerId);
  };

  const handleMapMarkerClick = useCallback(
    (dealerId: string) => {
      // Auto-reset filter if viewing stops only (so user can see the clicked recall)
      if (showStopsOnly) {
        setShowStopsOnly(false);
      }
      // Find the dealer from dealerLocations (map shows all dealers, not just visible ones)
      const dealer = dealerLocations.find(d => d.dealerId === dealerId);
      if (dealer) {
        // Bubble clicks select only — they don't expand the card. They do hoist
        // it to the top of the list and scroll to it, since the card is often
        // nowhere near the current scroll position.
        setPinnedDealer(dealer.dealerId);
        selectDealerCard(dealer.dealerId, { expand: false, scroll: true });
      }
    },
    [dealerLocations, showStopsOnly, selectDealerCard]
  );

  // Clicking a manual-stop bubble: switch to the Stops tab and scroll the
  // matching LocationGroup into view (mirrors handleMapMarkerClick for dealers)
  const handleManualStopMarkerClick = useCallback(
    (stopId: string) => {
      const stop = stagedManualStops.find(s => s.id === stopId);
      if (!stop) return;
      setScrollToStopLocation(getStopLocationKey(stop));
    },
    [stagedManualStops]
  );

  const handleDealerKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    dealer: DealerLocation
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDealerClick(dealer);
    }
  };

  const handleCopyPhone = useCallback(
    async (e: React.MouseEvent, phone: string | null) => {
      e.stopPropagation();
      if (!phone) return;
      try {
        await navigator.clipboard.writeText(phone);
        setCopiedPhone(phone);
        toast({ title: 'Copied!', description: 'Phone number copied to clipboard' });
        setTimeout(() => setCopiedPhone(null), 2000);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to copy phone number',
          variant: 'destructive'
        });
      }
    },
    [toast]
  );

  const handleMapBoundsChange = useCallback(
    async (bounds: MapBounds) => {
      setMapBounds(bounds);
      mapBoundsRef.current = bounds;
      setMapFilterActive(true);
      mapFilterActiveRef.current = true;

      // If this is the first bounds change and we haven't loaded initial data yet, trigger initial load
      if (!initialLoadTriggeredRef.current && preferencesLoaded && tenantId) {
        initialLoadTriggeredRef.current = true;
      }

      // Load data for this viewport
      setIsLoadingViewport(true);
      try {
        // Load full data (same for internal and non-internal tenants)
        if (loadViewportDataRef.current) {
          await loadViewportDataRef.current(bounds);
        }
      } finally {
        setIsLoadingViewport(false);
      }
    },
    [preferencesLoaded, tenantId]
  );

  // Load initial viewport after preferences and tenant context are ready
  // Wait for map to report its actual bounds instead of using hardcoded bounds
  // The map component will call handleMapBoundsChange when it's ready, which will trigger the initial load
  // If the map doesn't report bounds within 2 seconds, use a fallback to ensure data loads
  useEffect(() => {
    if (!preferencesLoaded || !tenantId) {
      return;
    }
    if (initialLoadTriggeredRef.current) {
      return;
    }

    // If we already have map bounds from the map component, use those immediately
    if (mapBounds) {
      initialLoadTriggeredRef.current = true;
      if (loadViewportDataRef.current) {
        loadViewportDataRef.current(mapBounds);
      }
      return;
    }

    // Fallback: If map hasn't reported bounds after 2 seconds, load around where the map
    // opens (the tenant's location). This ensures data loads even if map component has issues
    const fallbackTimer = setTimeout(() => {
      if (!initialLoadTriggeredRef.current && loadViewportDataRef.current) {
        initialLoadTriggeredRef.current = true;
        const defaultBounds: MapBounds = boundsAroundCenter(
          mapCenterRef.current ?? DEFAULT_MAP_CENTER
        );
        loadViewportDataRef.current(defaultBounds);
      }
    }, 2000);

    return () => clearTimeout(fallbackTimer);
  }, [preferencesLoaded, tenantId, mapBounds]);

  const clearMapFilter = () => {
    setMapBounds(null);
    setMapFilterActive(false);
  };

  const toggleDealerExpansion = (dealerId: string) => {
    captureScrollAnchor(dealerId);
    const newExpanded = new Set(expandedDealers);
    const isExpanding = !newExpanded.has(dealerId);
    if (newExpanded.has(dealerId)) {
      newExpanded.delete(dealerId);
    } else {
      newExpanded.add(dealerId);
      // When expanding a dealer that has no vehicle details (aggregate fast path), trigger full
      // recall load so we get recall-derived dealers with vehicles and the panel can show details.
      if (isExpanding && mapBoundsRef.current) {
        const dealer = dealerLocations.find(d => d.dealerId === dealerId);
        if (dealer && (dealer.vehicles || []).length === 0) {
          loadViewportDataRef.current(mapBoundsRef.current);
        }
      }
    }
    setExpandedDealers(newExpanded);
  };

  const dealerSummary = useMemo(() => {
    if (loading) {
      return 'Loading dealers...';
    }

    // Count unique recalls: a unique recall is identified by (VIN, campaign_number) pair
    const uniqueRecalls = new Set(
      filteredRecalls.map(recall => `${recall.vin}_${recall.campaign_number}`)
    ).size;

    const totalDealers = visibleDealerLocations.length;
    const totalRecalls = uniqueRecalls;

    return `${totalDealers.toLocaleString()} dealers • ${totalRecalls.toLocaleString()} recalls`;
  }, [filteredRecalls, loading, visibleDealerLocations]);

  // Stale-work nudge: assigned recalls whose expected visit date is in the past
  // and still not marked done in the route tracker. A non-zero count likely means
  // the dispatcher finished the work but hasn't updated statuses — nudge them.
  const overdueAssignedCount = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    let count = 0;
    filteredRecalls.forEach(recall => {
      const info = getRecallStatus(recall.vin, recall.campaign_number);
      if (info.status === 'available' || !info.expectedVisitDate) return;
      const due = new Date(info.expectedVisitDate);
      if (!isNaN(due.getTime()) && due < startOfToday) count++;
    });
    return count;
  }, [filteredRecalls, getRecallStatus]);

  const clearFilters = () => {
    setFilters({
      vin: '',
      make: '',
      model: '',
      year: '',
      dealer: '',
      campaigns: [],
      campaign_status: 'all',
      city: 'all',
      zip: 'all',
      mobile_eligible: 'all',
      region: '',
      priority: 'all',
      assignment_state: 'all'
    });
    setZipRadius('0');
    setSelectedDealer(null);
    setSelectedTechnicianIds([]);
    setColorByTechnician(false);
  };

  // Persist structural filter preferences per tenant across sessions.
  // Free-text search (vin/make/model/year/dealer) is intentionally NOT persisted
  // so a stale search doesn't reappear on load.
  const filterPrefsSnapshot = useMemo(
    () => ({
      city: filters.city,
      zip: filters.zip,
      zipRadius,
      campaigns: filters.campaigns,
      mobile_eligible: filters.mobile_eligible,
      campaign_status: filters.campaign_status,
      priority: filters.priority,
      assignment_state: filters.assignment_state,
      selectedTechnicianIds,
      colorByTechnician
    }),
    [
      filters.city,
      filters.zip,
      filters.campaigns,
      filters.mobile_eligible,
      filters.campaign_status,
      filters.priority,
      filters.assignment_state,
      zipRadius,
      selectedTechnicianIds,
      colorByTechnician
    ]
  );

  const restoreFilterPrefs = useCallback((restored: Partial<typeof filterPrefsSnapshot>) => {
    setFilters(
      prev =>
        ({
          ...prev,
          ...(restored.city !== undefined && { city: restored.city }),
          ...(restored.zip !== undefined && { zip: restored.zip }),
          ...(restored.campaigns !== undefined && { campaigns: restored.campaigns }),
          ...(restored.mobile_eligible !== undefined && {
            mobile_eligible: restored.mobile_eligible
          }),
          ...(restored.campaign_status !== undefined && {
            campaign_status: restored.campaign_status
          }),
          ...(restored.priority !== undefined && { priority: restored.priority }),
          ...(restored.assignment_state !== undefined && {
            assignment_state: restored.assignment_state
          })
        }) as typeof prev
    );
    if (restored.zipRadius !== undefined) setZipRadius(restored.zipRadius);
    if (restored.selectedTechnicianIds !== undefined) {
      setSelectedTechnicianIds(restored.selectedTechnicianIds);
    }
    if (restored.colorByTechnician !== undefined) {
      setColorByTechnician(restored.colorByTechnician);
    }
  }, []);

  usePersistedFilters('recall_map', filterPrefsSnapshot, restoreFilterPrefs);

  // Helper functions to count active filters per section
  const getVehicleFilterCount = () => {
    let count = 0;
    if (filters.vin) count++;
    if (filters.make) count++;
    if (filters.model) count++;
    if (filters.year) count++;
    return count;
  };

  const getLocationFilterCount = () => {
    let count = 0;
    if (filters.dealer) count++;
    if (filters.city && filters.city !== 'all') count++;
    if (filters.zip && filters.zip !== 'all') count++;
    if (zipRadius !== '0') count++;
    return count;
  };

  const getRecallFilterCount = () => {
    let count = 0;
    if (filters.campaigns?.length) count++;
    if (filters.priority && filters.priority !== 'all') count++;
    if (filters.mobile_eligible && filters.mobile_eligible !== 'all') count++;
    if (filters.campaign_status && filters.campaign_status !== 'all') count++;
    if (filters.assignment_state && filters.assignment_state !== 'all') count++;
    if (selectedTechnicianIds.length > 0) count++;
    return count;
  };

  const handleCopyVin = async (vin: string) => {
    if (!vin) {
      toast({
        title: 'Error',
        description: 'No VIN to copy',
        variant: 'destructive'
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(vin);
      showSuccessToast('VIN copied to clipboard!');
    } catch (error) {
      // Clipboard errors are typically not system errors, but log for debugging
      if (isSystemError(error)) {
        logSystemError(error, 'copying VIN to clipboard');
      }
      // Copy errors are non-critical, keep as toast
      toast({
        title: 'Error',
        description:
          'Failed to copy VIN to clipboard. Please ensure clipboard permissions are granted.',
        variant: 'destructive'
      });
    }
  };

  // Helper function to check if a recall is already selected
  const isRecallSelected = (vin: string, campaignNumber: string) => {
    return selectedRecalls.some(
      recall => recall.vin === vin && recall.campaign_number === campaignNumber
    );
  };

  // Helper function to show success toasts (keep for non-blocking notifications)
  const showSuccessToast = (message: string) => {
    toast({
      title: 'Success',
      description: message,
      variant: 'success'
    });
  };

  const handleAddToRoute = async (recall: RecallVehicle, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to add recalls to your route',
        variant: 'default'
      });
      return;
    }

    const recallKey = `${recall.vin}-${recall.campaign_number}`;

    // Check if already selected
    if (isRecallSelected(recall.vin, recall.campaign_number)) {
      toast({
        title: 'Warning',
        description: 'This recall is already in your route',
        variant: 'default'
      });
      return;
    }

    setLoadingRecalls(prev => new Set([...prev, recallKey]));
    // Clear any previous error for this recall
    setActionErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[recallKey];
      return newErrors;
    });

    try {
      await addRecall(recall);
      showSuccessToast('Recall added to route successfully!');
    } catch (error) {
      logSystemError(error, 'adding recall to route');
      const userMessage = isSystemError(error)
        ? translateSystemError(error, 'adding recall to route').message
        : 'Failed to add recall to route';
      // Set inline error for this recall
      setActionErrors(prev => ({
        ...prev,
        [recallKey]: userMessage
      }));
    } finally {
      setLoadingRecalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(recallKey);
        return newSet;
      });
    }
  };

  const handleRemoveFromRoute = async (recall: RecallVehicle, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to remove recalls from your route',
        variant: 'default'
      });
      return;
    }

    const recallKey = `${recall.vin}-${recall.campaign_number}`;

    // Check if not selected
    if (!isRecallSelected(recall.vin, recall.campaign_number)) {
      toast({
        title: 'Warning',
        description: 'This recall is not in your route',
        variant: 'default'
      });
      return;
    }

    setLoadingRecalls(prev => new Set([...prev, recallKey]));

    try {
      await removeRecall(recall.vin, recall.campaign_number);
      showSuccessToast('Recall removed from route successfully!');
    } catch (error) {
      logSystemError(error, 'removing recall from route');
      const { title, description } = describeActionError(error, 'removing this recall');
      toast({ title, description, variant: 'destructive' });
    } finally {
      setLoadingRecalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(recallKey);
        return newSet;
      });
    }
  };

  const handleRemoveRecallClick = (recall: RecallVehicle, event: React.MouseEvent) => {
    event.stopPropagation();

    // Security check: Ensure user is authenticated
    if (!isAuthenticated || !session?.user?.id) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to remove recalls',
        variant: 'default'
      });
      return;
    }

    setRecallToRemove(recall);
    setRemovalDialogOpen(true);
  };

  const handleConfirmRemoval = async (reason: RemovalReason, otherReason?: string) => {
    if (!recallToRemove || !session?.user?.id || !tenantId) {
      toast({
        title: 'Error',
        description: 'Missing required information to remove recall',
        variant: 'destructive'
      });
      return;
    }

    const recallKey = `${recallToRemove.vin}-${recallToRemove.campaign_number}`;
    const assignmentStatus = getRecallStatus(recallToRemove.vin, recallToRemove.campaign_number);

    // Capture old state for audit logging
    const oldState = {
      vin: recallToRemove.vin,
      campaign_number: recallToRemove.campaign_number,
      assignment_status: assignmentStatus?.status || 'available',
      assignment_id: assignmentStatus?.assignmentId || null
    };

    try {
      // Map removal reason to final status
      // One shared mapping for both pages. They used to decide this
      // separately and disagreed: the list sent `not_available`, the map
      // sent `not_there`, from the same click.
      const finalStatus = statusForRemovalReason(reason);
      const reasonText = reasonTextForRemoval(reason, otherReason);

      // If recall has an assignment, update via routeTrackingAPI
      if (assignmentStatus?.assignmentId) {
        const result = await routeTrackingAPI.updateRecallStatuses([
          {
            assignmentId: assignmentStatus.assignmentId,
            status: finalStatus,
            reason: reasonText
          }
        ]);

        if (!result.success) {
          throw new Error(result.error || 'Failed to update recall status');
        }

        // Audit log: Data modification for assigned recall
        logAdminAction(
          'recall_status_update',
          {
            resource: 'recall',
            resource_id: recallKey,
            assignment_id: assignmentStatus.assignmentId,
            vin: recallToRemove.vin,
            campaign_number: recallToRemove.campaign_number
          },
          {
            old_status: oldState.assignment_status,
            new_status: finalStatus,
            reason: reason,
            other_reason: otherReason,
            tenant_id: tenantId,
            timestamp: new Date().toISOString()
          }
        ).catch(err => {
          // eslint-disable-next-line no-console
          console.warn('Audit logging failed (non-blocking):', err);
        });
      } else {
        // If no assignment, create lifecycle record directly
        // For unassigned recalls, we create a lifecycle record to track the final status
        try {
          const { getProdSupabaseClient } = await import('@/lib/supabase/client');
          const { getTenantIdFromSession } = await import('@/lib/supabase/tenantScoping');
          const prodClient = getProdSupabaseClient();
          const resolvedTenantId = await getTenantIdFromSession(prodClient);

          if (!resolvedTenantId) {
            throw new Error('Unable to determine tenant association');
          }

          // Security: Verify tenant matches context tenant (defense in depth)
          if (tenantId && resolvedTenantId !== tenantId) {
            // eslint-disable-next-line no-console
            console.error('Tenant mismatch detected:', {
              contextTenantId: tenantId,
              sessionTenantId: resolvedTenantId,
              vin: recallToRemove.vin
            });
            throw new Error('Tenant verification failed');
          }

          // Guarded write: cannot move a recall out of a terminal state, cannot
          // lose a concurrent update, and its errors are logged. This replaces a
          // direct check-then-act insert/update that bypassed the guard entirely.
          await updateLifecycleOnManualStatusChange(
            recallToRemove.vin,
            recallToRemove.campaign_number,
            { finalStatus, ...(reasonText ? { otherReason: reasonText } : {}) }
          );

          // Audit log: Data modification for unassigned recall
          logAdminAction(
            'recall_lifecycle_update',
            {
              resource: 'recall',
              resource_id: recallKey,
              vin: recallToRemove.vin,
              campaign_number: recallToRemove.campaign_number
            },
            {
              old_status: 'available',
              new_status: finalStatus,
              reason: reason,
              other_reason: otherReason,
              tenant_id: resolvedTenantId,
              timestamp: new Date().toISOString()
            }
          ).catch(err => {
            // eslint-disable-next-line no-console
            console.warn('Audit logging failed (non-blocking):', err);
          });
        } catch (lifecycleError) {
          // eslint-disable-next-line no-console
          console.error('Error creating lifecycle record:', lifecycleError);
          // Log security violation if tenant scoping fails
          const { logSecurityViolation } = await import('@/lib/security/audit');
          logSecurityViolation('tenant_scoping_failure', {
            error: lifecycleError instanceof Error ? lifecycleError.message : 'Unknown error',
            vin: recallToRemove.vin,
            campaign_number: recallToRemove.campaign_number,
            tenant_id: tenantId
          }).catch(() => {
            // Ignore audit logging errors
          });
          throw lifecycleError;
        }
      }

      // Invalidate caches so both map and list pages pick up the change
      if (tenantId) {
        removeCache('user_preferences', tenantId);
        removeCache('raw_recalls', tenantId);
      }

      // Close dialog immediately (instant feedback pattern)
      setRemovalDialogOpen(false);
      toast({
        title: 'Recall Removed',
        description: 'The recall has been removed from the view.',
        variant: 'success'
      });

      // Start fade-out animation
      setFadingOutRecalls(prev => new Set([...prev, recallKey]));

      // After animation completes, remove from display
      setTimeout(() => {
        setRemovedRecalls(prev => new Set([...prev, recallKey]));
        setFadingOutRecalls(prev => {
          const newSet = new Set(prev);
          newSet.delete(recallKey);
          return newSet;
        });
      }, 300); // Match animation duration
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error removing recall:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove recall',
        variant: 'destructive'
      });
    } finally {
      setRecallToRemove(null);
    }
  };

  // Handle manual stop submission (instant-feedback pattern, supports multi-vehicle)
  const handleAddManualStops = async (submission: ManualStopSubmission) => {
    const count = submission.vehicles.length;
    // Customer names stay out of toasts — screens get shared.
    const label =
      submission.stop_kind === 'customer'
        ? `Customer stop in ${submission.location.city}`
        : `"${submission.location.name}"`;
    setAddStopSheetOpen(false);
    toast({
      title: count === 1 ? 'Manual Stop Added' : `${count} Manual Stops Added`,
      description:
        count === 1
          ? `${label} has been added to your route.`
          : `${label} (${count} vehicles) added to your route.`,
      variant: 'success'
    });
    try {
      await addManualStop(submission);
    } catch (error) {
      toast({
        title: 'Failed to Add Stop',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
      // Re-open the sheet so the user can correct and retry
      setAddStopSheetOpen(true);
      throw error;
    }
  };

  // Notes handling functions (dealer-level)
  const handleToggleNotes = (dealerName: string) => {
    setNotesOpen(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealerName)) {
        newSet.delete(dealerName);
      } else {
        newSet.add(dealerName);
      }
      return newSet;
    });
  };

  const handleSaveNotes = async (
    dealerName: string,
    notes: { advisor_notes: string; technician_notes: string }
  ) => {
    try {
      await updateNotes(dealerName, notes);
      showSuccessToast('Dealer notes saved successfully!');

      // Close the notes panel
      setNotesOpen(prev => {
        const newSet = new Set(prev);
        newSet.delete(dealerName);
        return newSet;
      });
    } catch (error) {
      logSystemError(error, 'saving dealer notes');
      let userMessage = 'Failed to save notes';
      if (error instanceof DealerConfigError) userMessage = error.message;
      else if (isSystemError(error)) {
        userMessage = translateSystemError(error, 'saving dealer notes').message;
      }
      toast({
        title: 'Error',
        description: userMessage,
        variant: 'destructive'
      });
    }
  };

  const handleCancelNotes = (dealerName: string) => {
    setNotesOpen(prev => {
      const newSet = new Set(prev);
      newSet.delete(dealerName);
      return newSet;
    });
  };

  // Track initial data load to prevent map unmounting
  // CRITICAL: This effect ensures the map doesn't unmount when navigating to empty areas
  useEffect(() => {
    if (!hasInitiallyLoaded && recalls.length > 0) {
      setHasInitiallyLoaded(true);
    }
  }, [hasInitiallyLoaded, recalls.length]);

  // Audit logging: Log data access when recalls are loaded for the first time
  // Note: We log once when data is first loaded, not on every viewport change to avoid spam
  useEffect(() => {
    if (recalls.length > 0 && hasInitiallyLoaded && tenantId) {
      logDataAccess({
        resource: 'recall_map_viewport',
        action: 'view',
        metadata: {
          // prettier-ignore
          viewport: mapBounds
            ? {
              north: mapBounds.north,
              south: mapBounds.south,
              east: mapBounds.east,
              west: mapBounds.west
            }
            : undefined,
          tenantId
        }
      });
    }
    // Only log once per initial load when hasInitiallyLoaded becomes true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitiallyLoaded]);

  // Improved loading UX: Show map immediately with cached data if available
  // Only show full loading screen if we have no data at all and are loading from DB
  // Note: We no longer use aggregate data for internal tenants, so all tenants behave the same
  const showFullLoadingScreen =
    (loading && !hasInitiallyLoaded && recalls.length === 0 && !isCacheBuilding) ||
    !mapMountAllowedRef.current;

  // Only show loading screen on initial load, not when loading new viewport data
  // WARNING: Changing this condition can cause the map to reset position!
  // The hasInitiallyLoaded check prevents unmounting during viewport updates
  return (
    <SecurityBoundary
      requiredPermissions={[{ resource: 'recalls', action: 'read', scope: PermissionScope.SELF }]}
      auditPageAccess='recall-map'
      tenantIsolation={true}
    >
      {showFullLoadingScreen && (
        <div className='flex flex-col justify-center items-center min-h-[60vh] gap-4'>
          <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-primary' />
          <p className='text-sm text-muted-foreground'>Loading initial map data...</p>
        </div>
      )}

      {/* A load error never unmounts the map; it shows as an overlay with a retry. */}
      {!showFullLoadingScreen && (
        <div className='h-full w-full flex flex-col overflow-hidden'>
          {/* Compact persistent filter bar (replaces header + filter panel + stats) */}
          <RecallFilterBar
            values={filters}
            onChange={patch => setFilters(prev => ({ ...prev, ...patch }) as typeof prev)}
            zipRadius={zipRadius}
            onZipRadiusChange={setZipRadius}
            selectedTechnicianIds={selectedTechnicianIds}
            onTechniciansChange={setSelectedTechnicianIds}
            colorByTechnician={colorByTechnician}
            onColorByTechnicianChange={setColorByTechnician}
            campaignsOptions={uniqueCampaigns}
            cityOptions={uniqueCities}
            zipOptions={uniqueZipCodes}
            technicians={technicians}
            getTechnicianColor={getTechnicianColor}
            activeFilterCount={
              getVehicleFilterCount() + getLocationFilterCount() + getRecallFilterCount()
            }
            onClearAll={clearFilters}
          />

          {/* Main Content - Map and Details */}
          <div className='flex-1 overflow-hidden min-h-0'>
            <div className='flex h-full'>
              {/* Map */}
              <div className='flex-1 h-full relative'>
                <Card className='h-full border-0 rounded-none'>
                  <CardContent className='p-0 h-full'>
                    {/* Debug info removed to prevent re-renders */}

                    {/* Working map with real data */}
                    <WorkingMap
                      key='stable-map-instance' // CRITICAL: Stable key prevents map remounting
                      dealerLocations={dealerLocationsWithSummary}
                      onMapBoundsChange={handleMapBoundsChange}
                      onDealerClick={handleMapMarkerClick}
                      onManualStopClick={handleManualStopMarkerClick}
                      {...(mapCenter && { initialCenter: mapCenter })}
                      flyToTarget={flyToTarget}
                      stagedManualStops={visibleManualStops}
                      summaryVersion={summaryVersion}
                      selectedTechnicianIds={selectedTechnicianIds}
                      colorByTechnician={colorByTechnician}
                      getTechnicianColor={getTechnicianColor}
                    />

                    {dataLoadError && (
                      <div className='absolute top-14 left-1/2 -translate-x-1/2 z-[1001] w-[min(32rem,calc(100%-2rem))]'>
                        <ErrorBanner
                          title='Some vehicles may be missing'
                          message="We couldn't load the latest recall data."
                          onRetry={refetchFullDataset}
                          dismissible={false}
                          className='bg-card/95 backdrop-blur-sm'
                        />
                      </div>
                    )}

                    {/* Stats chip (top-center, clear of zoom controls + legend) + quiet nudge */}
                    <div className='absolute top-4 left-1/2 -translate-x-1/2 z-[1000] flex flex-col items-center gap-1.5'>
                      <div className='flex items-center gap-2 rounded-full border bg-card/95 px-3 py-1 shadow-sm backdrop-blur-sm'>
                        <span className='text-xs font-medium text-foreground'>
                          {dealerSummary}
                          {assignmentTotals.unassigned > 0 && (
                            <span className='text-muted-foreground'>
                              {' • '}
                              {assignmentTotals.unassigned.toLocaleString()} unassigned
                            </span>
                          )}
                        </span>
                        {isLoadingViewport && !hasCachedData && (
                          <Loader2 className='h-3 w-3 animate-spin text-primary' />
                        )}
                        {isCacheBuilding && recalls.length > 0 && (
                          <span className='relative flex h-2 w-2'>
                            <span className='absolute h-2 w-2 rounded-full bg-primary animate-ping' />
                            <span className='h-2 w-2 rounded-full bg-primary' />
                          </span>
                        )}
                      </div>
                      {overdueAssignedCount > 0 && (
                        <Link
                          to='/route-tracker'
                          className='flex items-center gap-1 rounded-full bg-card/95 px-2.5 py-1 text-[11px] text-muted-foreground shadow-sm backdrop-blur-sm hover:text-foreground'
                          title='These assigned recalls are past their visit date. If completed, update them in Route Tracker.'
                        >
                          <AlertCircle className='h-3 w-3 text-amber-500' />
                          {overdueAssignedCount.toLocaleString()} past due — update in Route Tracker
                        </Link>
                      )}
                    </div>

                    {/* Assignment encoding legend (responsive overlay) */}
                    <MapLegend
                      colorByTechnician={colorByTechnician}
                      technicians={legendTechnicians}
                    />

                    {/* Loading Indicators - Overlay on Map */}
                    {/* Initial loading without cache */}
                    {loading && !hasCachedData && (
                      <div className='absolute inset-0 bg-background/50 backdrop-blur-sm z-[999] flex items-center justify-center'>
                        <div className='bg-card border rounded-lg shadow-lg px-6 py-4 flex flex-col items-center gap-3'>
                          <Loader2 className='h-8 w-8 animate-spin text-primary' />
                          <p className='text-sm text-muted-foreground'>Loading map data...</p>
                        </div>
                      </div>
                    )}

                    {/* Loading more data indicator (bottom-right, clear of the top-right legend) */}
                    {isLoadingMore && (
                      <div className='absolute bottom-4 right-4 z-[1000]'>
                        <div className='bg-card/90 border rounded-lg shadow-md px-3 py-2 flex items-center gap-2'>
                          <div className='relative'>
                            <div className='h-2 w-2 bg-primary rounded-full animate-ping absolute' />
                            <div className='h-2 w-2 bg-primary rounded-full' />
                          </div>
                          <span className='text-xs text-muted-foreground'>Loading more...</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Dealership Details - Right Side (1/3 width) */}
              <div className='w-full sm:w-96 lg:w-1/3 h-full flex-shrink-0'>
                <Card className='h-full flex flex-col border-0 border-l rounded-none'>
                  <div className='flex items-center justify-between px-3 py-2 border-b sticky top-0 bg-card z-10 flex-shrink-0'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className='text-sm font-medium truncate'>Locations</span>
                      <Badge variant='secondary' className='h-5 px-1.5 text-xs'>
                        {filteredDealerLocations.length + uniqueStopLocationNames.size}
                      </Badge>
                      {mapFilterActive && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type='button'
                                onClick={clearMapFilter}
                                aria-label='Showing locations in the current map area — click to show all'
                                className='inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1'
                              >
                                <MapPin className='h-3.5 w-3.5' aria-hidden='true' />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Showing locations in the current map area — click to show all
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className='flex items-center gap-1.5 shrink-0'>
                      {uniqueStopLocationNames.size > 0 && (
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => {
                            if (showStopsOnly) {
                              setShowStopsOnly(false);
                              clearMapFilter();
                            } else {
                              setShowStopsOnly(true);
                            }
                          }}
                          className='text-xs h-7 px-2'
                        >
                          {showStopsOnly ? 'Show All' : 'View Manual Stops'}
                        </Button>
                      )}
                      {isDispatcher && (
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => setAddStopSheetOpen(true)}
                          className='text-xs h-7 px-2'
                        >
                          <Plus className='h-3.5 w-3.5 mr-1' />
                          Add Manual Stop
                        </Button>
                      )}
                    </div>
                  </div>

                  <div
                    ref={listScrollRef}
                    className='flex-1 overflow-y-auto p-2 space-y-4'
                    style={{ minHeight: 0 }}
                  >
                    <div className='space-y-2'>
                      {orderedDealerLocations.map((dealer, dealerIndex) => (
                        <div
                          key={dealer.dealerId}
                          ref={el => {
                            if (el) {
                              dealerCardRefs.current.set(dealer.dealerId, el);
                            } else {
                              dealerCardRefs.current.delete(dealer.dealerId);
                            }
                          }}
                          className='animate-in fade-in slide-in-from-right-2 duration-300'
                          style={{
                            animationDelay: `${Math.min(dealerIndex * 50, 500)}ms`,
                            animationFillMode: 'backwards'
                          }}
                        >
                          <div
                            role='button'
                            tabIndex={0}
                            onClick={() => handleDealerClick(dealer)}
                            onKeyDown={event => handleDealerKeyDown(event, dealer)}
                            className={`border rounded-lg p-2 cursor-pointer transition-all duration-200 ${
                              selectedDealer === dealer.dealerId
                                ? 'border-primary bg-primary/5'
                                : 'border hover:bg-muted'
                            }`}
                          >
                            <div className='space-y-1 mb-2'>
                              <div className='flex justify-between items-start'>
                                <h4 className='font-medium text-base flex-1'>
                                  {dealer.dealerName || dealer.name}
                                </h4>
                                <div className='flex items-center gap-1 flex-shrink-0'>
                                  {/* Keep the notes toggle from reaching the card's
                                      click/keydown handlers: selecting a dealer pins its
                                      card to the top of the list and scrolls it into view,
                                      so the list would jump just from opening notes. */}
                                  <div
                                    role='presentation'
                                    onClick={e => e.stopPropagation()}
                                    onKeyDown={e => e.stopPropagation()}
                                  >
                                    <NotesIndicator
                                      hasAdvisorNotes={hasAdvisorNotes(dealer.dealerName)}
                                      hasTechnicianNotes={hasTechnicianNotes(dealer.dealerName)}
                                      onClick={() => handleToggleNotes(dealer.dealerName)}
                                      disabled={notesLoading}
                                    />
                                  </div>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      toggleDealerExpansion(dealer.dealerId);
                                    }}
                                    className='-mt-1'
                                  >
                                    {expandedDealers.has(dealer.dealerId) ? (
                                      <ChevronUp className='h-4 w-4' />
                                    ) : (
                                      <ChevronDown className='h-4 w-4' />
                                    )}
                                  </Button>
                                </div>
                              </div>

                              {/* Phone number - click to copy */}
                              {dealer.phone && (
                                <button
                                  type='button'
                                  onClick={e => handleCopyPhone(e, dealer.phone)}
                                  className='flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm'
                                  title='Click to copy phone number'
                                  aria-label={`Copy phone number ${formatPhoneNumber(dealer.phone)}`}
                                >
                                  <span className='hover:underline'>
                                    {formatPhoneNumber(dealer.phone)}
                                  </span>
                                  {copiedPhone === dealer.phone && (
                                    <Check className='h-3 w-3 text-green-500' aria-hidden='true' />
                                  )}
                                </button>
                              )}

                              {(() => {
                                const address = dealer.address;
                                return (
                                  address &&
                                  address !== 'Address not available' &&
                                  address.trim() !== '' && (
                                    <p className='text-sm text-muted-foreground leading-relaxed'>
                                      {address}
                                    </p>
                                  )
                                );
                              })()}
                            </div>

                            <div className='flex gap-1.5 flex-wrap'>
                              <Badge
                                variant='outline'
                                className='text-xs bg-red-50 text-red-700 border-red-200'
                                title={`Dealer panel count for ${dealer.dealerName}: ${dealer.recallCount} recalls`}
                              >
                                <AlertCircle className='h-3 w-3 mr-1' />
                                {dealer.recallCount}{' '}
                                {dealer.recallCount === 1 ? 'recall' : 'recalls'}
                              </Badge>
                              <Badge
                                variant='outline'
                                className='text-xs bg-blue-50 text-blue-700 border-blue-200'
                              >
                                <Car className='h-3 w-3 mr-1' />
                                {dealer.vehicleCount}{' '}
                                {dealer.vehicleCount === 1 ? 'vehicle' : 'vehicles'}
                              </Badge>
                            </div>
                          </div>

                          {/* Dealer Notes Panel */}
                          {dealer.dealerName && notesOpen.has(dealer.dealerName) && (
                            <div className='mt-2 ml-3 mr-1'>
                              <DealerNotesPanel
                                dealerName={dealer.dealerName}
                                initialNotes={getNotes(dealer.dealerName)}
                                onSave={notes =>
                                  handleSaveNotes(dealer.dealerName as string, notes)
                                }
                                onCancel={() => handleCancelNotes(dealer.dealerName as string)}
                                readOnly={notesReadOnly}
                              />
                            </div>
                          )}

                          {expandedDealers.has(dealer.dealerId) && (
                            <div className='mt-2 space-y-3'>
                              {(dealer.vehicles || []).length === 0 ? (
                                <p className='text-sm text-muted-foreground ml-3 mr-1 py-2'>
                                  {loading
                                    ? 'Loading recall details...'
                                    : 'Recall details will appear once data has finished loading. Try again in a moment.'}
                                </p>
                              ) : (
                                <>
                                  {(dealer.vehicles || []).map((vehicle, index) => {
                                    const recalls = vehicle.recalls || [vehicle];
                                    const recallCount = recalls.length;
                                    const vehicleKey =
                                      vehicle.vin ??
                                      `${dealer.dealerId}-${vehicle.carYear ?? index}`;

                                    return (
                                      <div
                                        key={vehicleKey}
                                        className='ml-3 mr-1 p-3 border-l-4 border-l-blue-400 bg-muted/30 border rounded-r-lg shadow-sm hover:shadow-md transition-shadow'
                                      >
                                        <div className='flex justify-between items-start mb-3'>
                                          <div className='flex items-center gap-2'>
                                            <div className='w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center'>
                                              {index + 1}
                                            </div>
                                            <h5 className='font-semibold text-base text-foreground'>
                                              {vehicle.carYear} {vehicle.makeName}{' '}
                                              {vehicle.modelName}
                                            </h5>
                                          </div>
                                        </div>

                                        <div className='flex items-center gap-2 mb-3 pb-2 border-b border-border'>
                                          <span className='text-sm font-mono text-muted-foreground bg-gray-100 dark:bg-gray-600 px-2 py-0.5 rounded'>
                                            {vehicle.vin}
                                          </span>
                                          <Button
                                            variant='ghost'
                                            size='sm'
                                            onClick={() => handleCopyVin(vehicle.vin)}
                                            title='Copy VIN'
                                            className='h-6 w-6 p-0'
                                          >
                                            <Copy className='h-3 w-3' />
                                          </Button>
                                          {recallCount > 1 && (
                                            <Badge
                                              variant='outline'
                                              className='text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700'
                                            >
                                              {recallCount} recalls
                                            </Badge>
                                          )}
                                        </div>

                                        <div className='space-y-2'>
                                          {recalls.map((recall: RecallVehicle) => {
                                            const recallKey = `${recall.vin}-${recall.campaign_number}`;
                                            const isLoading = loadingRecalls.has(recallKey);
                                            const isSelected = isRecallSelected(
                                              recall.vin,
                                              recall.campaign_number
                                            );
                                            const assignmentStatus = getRecallStatus(
                                              recall.vin,
                                              recall.campaign_number
                                            );
                                            const isAssignedOrBeyond = [
                                              'assigned',
                                              'confirmed',
                                              'in_progress',
                                              'completed'
                                            ].includes(assignmentStatus?.status || '');
                                            const campaignNumber = recall.campaign_number || '';
                                            const getCampaignStatus = (
                                              num: string
                                            ): 'Available' | 'Advance Notice' | 'Paused' | null => {
                                              if (!num) return null;
                                              if (campaignStatus[num]) return campaignStatus[num];
                                              const normalized = num.toUpperCase();
                                              const foundKey = Object.keys(campaignStatus).find(
                                                key => key.toUpperCase() === normalized
                                              );
                                              return foundKey
                                                ? (campaignStatus[foundKey] ?? null)
                                                : null;
                                            };
                                            // Trust the resolved campaign-level status: get_tenant_campaigns RPC
                                            // + deduplicateCampaigns already merge admin/tenant overrides with the
                                            // current user's own preferences. Only fall back to the raw per-recall
                                            // flag when no campaign-level status is available.
                                            let hasAdvanceNotice: boolean;
                                            const resolvedCampaignStatus =
                                              getCampaignStatus(campaignNumber);
                                            if (resolvedCampaignStatus !== null) {
                                              hasAdvanceNotice =
                                                resolvedCampaignStatus === 'Advance Notice';
                                            } else {
                                              hasAdvanceNotice = Boolean(
                                                recall.has_advance_notice_recalls
                                              );
                                            }
                                            const isRemoved = removedRecalls.has(recallKey);
                                            const isFadingOut = fadingOutRecalls.has(recallKey);
                                            const isPausedForRecall = campaignNumber
                                              ? campaignStatus[campaignNumber] === 'Paused'
                                              : false;
                                            const pauseReasonForRecall = campaignNumber
                                              ? campaignStatusReasons[campaignNumber]
                                              : undefined;

                                            // Skip rendering if removed
                                            if (isRemoved) {
                                              return null;
                                            }

                                            return (
                                              <div
                                                key={recallKey}
                                                className={`space-y-2 transition-opacity duration-300 ${
                                                  isFadingOut ? 'opacity-0' : 'opacity-100'
                                                }`}
                                              >
                                                <div className='relative flex items-center justify-between gap-2 p-2 rounded bg-card border border-blue-500/30 dark:border-blue-400/30 hover:border-primary hover:shadow-sm transition-all'>
                                                  <div className='flex-1 min-w-0'>
                                                    <div className='flex items-baseline gap-2'>
                                                      <Badge
                                                        variant='outline'
                                                        className='text-xs shrink-0 bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 dark:border-blue-400/30 inline-flex items-center gap-1'
                                                      >
                                                        {recall.campaign_number?.split('/')[0] ||
                                                          recall.campaign_number}
                                                        <AdvanceNoticeIndicator
                                                          hasAdvanceNotice={hasAdvanceNotice}
                                                          variant='icon'
                                                          size='sm'
                                                        />
                                                      </Badge>
                                                      <span className='text-sm text-foreground font-medium truncate'>
                                                        {recall.recall_title}
                                                      </span>
                                                      <PausedCampaignIndicator
                                                        isPaused={isPausedForRecall}
                                                        {...(pauseReasonForRecall && {
                                                          pauseReason: pauseReasonForRecall
                                                        })}
                                                        variant='icon'
                                                        size='sm'
                                                      />
                                                    </div>
                                                  </div>
                                                  <div className='flex items-center gap-1'>
                                                    <Button
                                                      variant='ghost'
                                                      size='sm'
                                                      onClick={e => {
                                                        if (
                                                          !hasAdvanceNotice &&
                                                          !isAssignedOrBeyond
                                                        ) {
                                                          isSelected
                                                            ? handleRemoveFromRoute(recall, e)
                                                            : handleAddToRoute(recall, e);
                                                        }
                                                      }}
                                                      disabled={
                                                        isLoading ||
                                                        isAssignedOrBeyond ||
                                                        hasAdvanceNotice
                                                      }
                                                      className='group h-8 w-8 p-0 flex-shrink-0 hover:bg-accent hover:text-accent-foreground transition-colors'
                                                      title={
                                                        hasAdvanceNotice
                                                          ? "This recall is not serviceable yet - it's an advance notice. Parts or service procedures may not be available at this time."
                                                          : isAssignedOrBeyond
                                                            ? `Already ${assignmentStatus?.status}${assignmentStatus?.technicianName ? ` to ${assignmentStatus?.technicianName}` : ''}`
                                                            : isSelected
                                                              ? 'Remove from route'
                                                              : 'Add to route'
                                                      }
                                                    >
                                                      {isLoading ? (
                                                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
                                                      ) : hasAdvanceNotice ? (
                                                        <AdvanceNoticeIndicator
                                                          hasAdvanceNotice={hasAdvanceNotice}
                                                          variant='icon'
                                                          size='sm'
                                                        />
                                                      ) : isSelected ? (
                                                        <Check className='h-4 w-4 text-green-600 dark:text-green-500 group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors' />
                                                      ) : isAssignedOrBeyond ? (
                                                        <Check className='h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors' />
                                                      ) : (
                                                        <Plus className='h-4 w-4 text-primary group-hover:text-primary/90 dark:group-hover:text-primary transition-colors' />
                                                      )}
                                                    </Button>
                                                    {/* Remove Recall Button (X) */}
                                                    <Button
                                                      variant='ghost'
                                                      size='sm'
                                                      onClick={e =>
                                                        handleRemoveRecallClick(recall, e)
                                                      }
                                                      className='h-6 w-6 p-0 flex-shrink-0 hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors'
                                                      title='Remove recall'
                                                    >
                                                      <X className='h-3.5 w-3.5' />
                                                    </Button>
                                                    {actionErrors[
                                                      `${recall.vin}-${recall.campaign_number}`
                                                    ] && (
                                                      <InlineError
                                                        message={
                                                          actionErrors[
                                                            `${recall.vin}-${recall.campaign_number}`
                                                          ] ?? ''
                                                        }
                                                        className='text-xs mt-1'
                                                      />
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <ManualStopsList
                      stops={viewportFilteredManualStops}
                      onRemove={removeManualStop}
                      assignedStops={assignedManualStops}
                      scrollToLocation={scrollToStopLocation}
                      onScrollComplete={() => setScrollToStopLocation(null)}
                      kindFilter={stopKindFilter}
                      onKindFilterChange={setStopKindFilter}
                      kindCounts={manualStopKindCounts}
                    />
                  </div>
                </Card>

                <Sheet open={addStopSheetOpen} onOpenChange={setAddStopSheetOpen}>
                  <SheetContent side='right' className='w-full sm:max-w-xl overflow-y-auto p-0'>
                    <ManualStopForm onSubmit={handleAddManualStops} />
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Recall Dialog */}
      <RemoveRecallDialog
        open={removalDialogOpen}
        onOpenChange={setRemovalDialogOpen}
        onConfirm={handleConfirmRemoval}
        recallTitle={recallToRemove?.recall_title ?? ''}
        campaignNumber={recallToRemove?.campaign_number ?? ''}
        vin={recallToRemove?.vin ?? ''}
      />
    </SecurityBoundary>
  );
};

export default SimpleRecallMapPage;
